import { defineComponent as _defineComponent } from 'vue'
import { BModal } from 'bootstrap-vue';
import { IncurCostsType, incurCostsTypeLabels, Trade, TradeStatus } from 'ah-api-gateways';
import { mergeMap } from 'rxjs/operators';
import { waitForEntityChange } from 'ah-requests';
import { radioField } from 'ah-common-lib/src/form/models';
import { makeFormModel } from 'ah-common-lib/src/form/helpers';
import { MINUTE } from 'ah-common-lib/src/constants/time';
import { FormDefinition } from 'ah-common-lib/src/form/interfaces';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import { reactive, ref, computed, getCurrentInstance } from 'vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useTradeState } from '../..';

import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';


export default /*#__PURE__*/_defineComponent({
  __name: 'CancelTradeModal',
  props: {
    trade: null
  },
  emits: ["cancel-trade-executed"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'cancel-trade-executed'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  trade: Trade;
};

const toast = getCurrentInstance()?.proxy.$toast;
const tradeState = useTradeState();
const onBehalfOfClient = useOnBehalfOf();





const cancelTradeModal = ref<BModal | null>(null);

const state = reactive({
  closingCost: null as number | null,
  modalShown: false as boolean,
  lpCancelPnlAmount: null as number | null,
  lpCancelPnlAmountCurrency: null as string | null,
  refreshComponentTimeout: null as number | null,
});

const requestManager = useRequestManager({
  exposeToParent: ['cancelTradeRequest'],
  onRetryFromParentManager: (k: string) => {
    if (k === 'cancelTradeRequest') {
      continueCancelTrade();
    }
  },
});

const incurTypeOptions = computed(() => {
  return Object.keys(incurCostsTypeLabels).map((k) => {
    return {
      value: k,
      label: incurCostsTypeLabels[k as IncurCostsType],
    };
  });
});

const incurCostsForm = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'incurCostsForm',
    fieldType: 'form',
    fields: [
      radioField('incurCost', '', incurTypeOptions.value, {
        required: true,
        clearValue: '',
        defaultValue: IncurCostsType.AH,
        fieldWrapperClass: 'radio-form-field',
      }),
    ],
  }),
  validation: null,
});

function loadTradeCancelationCosts() {
  return requestManager.manager
    .sameOrCancelAndNew(
      'getTrade',
      tradeState.services.trade.getTradeCostsOfClosing(props.trade.id, { errors: { silent: true } })
    )
    .toPromise()
    .then(
      (cost) => {
        state.lpCancelPnlAmountCurrency = cost.currency;
        state.lpCancelPnlAmount = cost.lpCancelPnlAmount;
        state.closingCost = cost.cancelFeeGbpAmount;
      },
      (e) => {
        if (e.response?.status !== 502) {
          toast?.error('An unexpected problem has occurred. Please try again later.');
        }
        if (e.response?.status === 502) {
          toast?.error(e.response?.data.message);
        }
        throw e;
      }
    );
}

function hide() {
  state.modalShown = false;
}

function refreshComponent() {
  state.refreshComponentTimeout = window.setTimeout(() => {
    if (state.modalShown) {
      loadTradeCancelationCosts()
        .catch(() => {
          hide();
        })
        .finally(() => {
          if (state.modalShown) {
            refreshComponent();
          }
        });
    }
  }, MINUTE);
}

/**
 * Display cancellation modal if costs are available
 */
function tryDisplayCancelModal() {
  if (!state.modalShown) {
    loadTradeCancelationCosts().then(() => {
      state.modalShown = true;
      refreshComponent();
    });
  }
}

function clearComponentRefresh() {
  if (state.refreshComponentTimeout !== null) {
    clearTimeout(state.refreshComponentTimeout);
    state.refreshComponentTimeout = null;
  }
}

function onModalHidden() {
  state.closingCost = null;
  state.lpCancelPnlAmount = null;
  state.lpCancelPnlAmountCurrency = null;
  clearComponentRefresh();
}

const isAHUser = computed(() => tradeState.store.useAuthStore().isAHUser);

const incurCost = computed(() => {
  if (incurCostsForm) {
    return incurCostsForm.form.incurCost;
  }

  return null;
});

function continueCancelTrade() {
  if (!onBehalfOfClient.value || !isAHUser.value) {
    throw 'Trade cancelation is an admin action only';
  }
  clearComponentRefresh();

  requestManager.manager
    .cancelAndNew(
      'cancelTradeRequest',
      tradeState.services.trade
        .cancelTrade(props.trade.id, onBehalfOfClient.value!.id, isAHUser.value, incurCost.value)
        .pipe(
          mergeMap(() =>
            waitForEntityChange(
              () => tradeState.services.trade.getTrade(props.trade.id, { errors: { silent: true } }),
              (response) =>
                response.status === TradeStatus.CANCELED || response.status === TradeStatus.REMAINING_CANCELED
            )
          )
        )
    )
    .subscribe(
      () => {
        tradeState.toast.success('Trade cancelled successfully');
        emit('cancel-trade-executed');
        hide();
      },
      () => {
        refreshComponent();
      }
    );
}

return { __sfc: true,toast, tradeState, onBehalfOfClient, emit, props, cancelTradeModal, state, requestManager, incurTypeOptions, incurCostsForm, loadTradeCancelationCosts, hide, refreshComponent, tryDisplayCancelModal, clearComponentRefresh, onModalHidden, isAHUser, incurCost, continueCancelTrade, BModal, formatCurrencyValue }
}

})